// Helpers for the sidebar tree. The API returns a flat list; the client builds
// parent/child relationships from `parent` (null = top-level notebook).

export function parentIdOf(page) {
  if (!page?.parent) return null
  return String(page.parent._id || page.parent)
}

function compareTitle(a, b) {
  const left = (a.title || 'Untitled').localeCompare(b.title || 'Untitled', undefined, {
    sensitivity: 'base',
  })
  if (left !== 0) return left
  return String(a._id).localeCompare(String(b._id))
}

function childrenMap(pages) {
  const map = new Map()
  pages.forEach((page) => {
    if (!page?._id) return
    const key = parentIdOf(page) || ''
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(page)
  })
  map.forEach((list) => list.sort(compareTitle))
  return map
}

// Depth-first rows for the visible (expanded) tree. Each row is
// { page, depth, hasChildren }. `rootId` drills the list to that page's
// children (the page itself is the location, not a row).
export function flattenVisible(pages, expandedIds, rootId) {
  const children = childrenMap(pages)
  const rows = []
  const walk = (node, depth) => {
    const kids = children.get(String(node._id)) || []
    rows.push({ page: node, depth, hasChildren: kids.length > 0 })
    if (expandedIds.has(String(node._id))) {
      kids.forEach((child) => walk(child, depth + 1))
    }
  }
  const roots = rootId ? children.get(String(rootId)) || [] : children.get('') || []
  roots.forEach((root) => walk(root, 0))
  return rows
}

// The drilled page and its ancestors, root first. Empty when not drilled.
export function burrowTrail(pages, rootId) {
  if (!rootId) return []
  const byId = new Map((pages || []).filter((page) => page?._id).map((page) => [String(page._id), page]))
  const chain = []
  let current = byId.get(String(rootId))
  while (current) {
    chain.push(current)
    const pid = parentIdOf(current)
    current = pid ? byId.get(pid) : null
  }
  return chain.reverse()
}

export function ancestorIds(pages, id) {
  if (!id) return []
  const byId = new Map((pages || []).filter((page) => page?._id).map((page) => [String(page._id), page]))
  const out = []
  let current = byId.get(String(id))
  while (current) {
    const pid = parentIdOf(current)
    if (!pid) break
    out.push(pid)
    current = byId.get(pid)
  }
  return out
}

export function collectSubtreeIds(pages, id) {
  const children = childrenMap(pages)
  const ids = [String(id)]
  const stack = [String(id)]
  while (stack.length) {
    const current = stack.pop()
    for (const child of children.get(current) || []) {
      const cid = String(child._id)
      ids.push(cid)
      stack.push(cid)
    }
  }
  return ids
}
